/**
 * Where the room is.
 *
 * On the web this is a question with an obvious answer — the page came from
 * the server, so the server is wherever the page came from — and the client
 * asked it that way for months: `/api/...` and `location.host`.
 *
 * That answer stops being true the moment the same client is packaged as an
 * application. A desktop or mobile build serves its own files from a local
 * scheme (`tauri://localhost`, or `http://tauri.localhost` on Windows), so a
 * request to `/api/profile` goes to the bundle rather than to the room, and a
 * socket to `location.host` dials the application itself.
 *
 * So the question is asked once, here, and answered differently depending on
 * how the client is running:
 *
 *   · in a browser, the origin it was served from — which keeps every
 *     deployment, including a local dev server against a local API, working
 *     exactly as it did;
 *   · in a packaged build, the room's own address, compiled in at build time
 *     and overridable for anyone running a build against a staging server.
 *
 * Nothing else in the client knows there is a question. It calls `api()` and
 * `socketUrl()` and gets a URL.
 */

/**
 * The address a packaged build talks to.
 *
 * Baked in by Vite at build time. A build with nothing set is a web build, and
 * a web build has no business overriding its own origin.
 */
// Read defensively, because `import.meta.env` is something Vite puts there
// rather than something the language provides: under plain Node — a test, a
// script — it is simply absent, and reading through it threw and took every
// module that imports this one down with it. An absent value is also exactly
// the right answer for a web build, so there is nothing to report.
const PACKAGED_ORIGIN = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[
    'VITE_SERVER_ORIGIN'
  ] ?? ''
).trim();

/**
 * Where a packaged build talks to when nothing says otherwise.
 *
 * An application is not a page: there is no origin to fall back on, and a
 * build that shipped without one would start, draw a login screen, and never
 * be able to reach anything — the worst kind of failure, because it looks like
 * a working application. So the room's own address is the default and the
 * environment variable is for pointing a build somewhere else.
 */
const ROOM = 'https://iranpokertour.online';

/**
 * True when the page was not served over the network.
 *
 * The three schemes a packaged webview uses. Checking the scheme rather than
 * asking a native API keeps this working in a plain browser, in a test, and in
 * whatever shell comes after the current one.
 */
function packaged(): boolean {
  if (typeof location === 'undefined') return false;
  const { protocol, hostname } = location;
  return (
    protocol === 'tauri:' ||
    protocol === 'file:' ||
    protocol === 'capacitor:' ||
    hostname === 'tauri.localhost'
  );
}

/** The room's origin: `https://iranpokertour.online`, or wherever this page came from. */
export function serverOrigin(): string {
  if (packaged()) return (PACKAGED_ORIGIN === '' ? ROOM : PACKAGED_ORIGIN).replace(/\/+$/, '');
  return location.origin;
}

/**
 * An API path, made absolute.
 *
 * Takes the same string the code has always passed — `/api/profile` — so the
 * call sites read the same and the only thing that changed is that they now go
 * somewhere in particular.
 */
export function api(path: string): string {
  return `${serverOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}

/** The game socket, on whichever host the room is. */
export function socketUrl(): string {
  const origin = serverOrigin();
  const scheme = origin.startsWith('https:') ? 'wss' : 'ws';
  return `${origin.replace(/^https?:/, scheme + ':')}/ws`;
}
